import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play, Calendar } from "lucide-react";
import { useGallery, useServices } from "@/lib/hooks";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { fileUrl } from "@/lib/api";

export default function Gallery() {
  const [category, setCategory] = useState("all");
  const [visible, setVisible] = useState(9);
  const [active, setActive] = useState(null); // index in filtered list
  const [lightboxView, setLightboxView] = useState("main"); // "main" (slider) or an extra image url
  const { data: items = [], isLoading } = useGallery(category === "all" ? null : category);
  const { data: services = [] } = useServices();

  const categories = useMemo(() => {
    const set = new Set(services.map((s) => s.name));
    return ["all", ...Array.from(set)];
  }, [services]);

  const shown = items.slice(0, visible);

  const openAt = (i) => setActive(i);
  const close = () => setActive(null);
  const prev = () => setActive((a) => (a > 0 ? a - 1 : items.length - 1));
  const next = () => setActive((a) => (a < items.length - 1 ? a + 1 : 0));
  const current = active !== null ? items[active] : null;

  useEffect(() => { setLightboxView("main"); }, [active]);

  return (
    <div data-testid="gallery-page">
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 md:py-20">
          <span className="text-green-700 font-bold uppercase tracking-widest text-xs">Our work</span>
          <h1 className="font-display font-black text-slate-900 text-4xl sm:text-5xl mt-4 tracking-tight">Before & after gallery</h1>
          <p className="mt-4 text-lg text-slate-500 max-w-2xl">Tap any project to open the interactive before/after slider.</p>

          <div className="mt-8 flex flex-wrap gap-2.5">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => { setCategory(cat); setVisible(9); }}
                data-testid={`gallery-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  category === cat ? "bg-green-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {cat === "all" ? "All Work" : cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        {isLoading ? (
          <p className="text-slate-500">Loading gallery…</p>
        ) : items.length === 0 ? (
          <p className="text-slate-500">No projects in this category yet.</p>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((item, i) => (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
                  onClick={() => openAt(i)}
                  data-testid={`gallery-item-${i}`}
                  className="group relative text-left rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="relative aspect-[4/3] grid grid-cols-2">
                    {item.video_url ? (
                      <div className="col-span-2 relative">
                        <video src={fileUrl(item.video_url)} className="h-full w-full object-cover" muted />
                        <div className="absolute inset-0 grid place-items-center bg-black/30">
                          <div className="h-14 w-14 rounded-full bg-white/90 grid place-items-center"><Play className="h-6 w-6 text-green-700 ml-0.5" /></div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <img src={fileUrl(item.before_image_url)} alt="Before" className="h-full w-full object-cover" loading="lazy" />
                        <img src={fileUrl(item.after_image_url)} alt="After" className="h-full w-full object-cover" loading="lazy" />
                        <span className="absolute bottom-2 left-2 rounded bg-slate-900/80 px-2 py-0.5 text-[10px] font-bold uppercase text-white">Before</span>
                        <span className="absolute bottom-2 right-2 rounded bg-green-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">After</span>
                      </>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="font-semibold text-slate-900 text-sm line-clamp-1">{item.caption}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                      <span className="text-green-700 font-semibold">{item.service_category}</span>
                      {item.completion_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{item.completion_date}</span>}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {visible < items.length && (
              <div className="mt-10 text-center">
                <button onClick={() => setVisible((v) => v + 9)} data-testid="gallery-load-more" className="rounded-full bg-slate-900 px-8 py-3 text-sm font-bold text-white hover:bg-green-700 transition-colors">
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* LIGHTBOX */}
      {current && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="gallery-lightbox" onClick={close}>
          <button onClick={close} className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20" data-testid="lightbox-close"><X className="h-6 w-6" /></button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-3 md:left-6 h-11 w-11 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20" data-testid="lightbox-prev"><ChevronLeft className="h-6 w-6" /></button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-3 md:right-6 h-11 w-11 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20" data-testid="lightbox-next"><ChevronRight className="h-6 w-6" /></button>

          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="rounded-xl overflow-hidden bg-black">
              {current.video_url ? (
                <video src={fileUrl(current.video_url)} controls autoPlay className="w-full max-h-[70vh]" />
              ) : lightboxView === "main" ? (
                <BeforeAfterSlider before={current.before_image_url} after={current.after_image_url} className="aspect-[16/10] max-h-[70vh]" />
              ) : (
                <img src={fileUrl(lightboxView)} alt="Project photo" className="w-full max-h-[70vh] object-contain bg-black" />
              )}
            </div>
            {!current.video_url && current.extra_images?.length > 0 && (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="lightbox-thumbs">
                <button onClick={() => setLightboxView("main")} data-testid="lightbox-thumb-main" className={`relative shrink-0 h-16 w-24 rounded-lg overflow-hidden border-2 grid grid-cols-2 ${lightboxView === "main" ? "border-green-500" : "border-transparent opacity-70 hover:opacity-100"}`}>
                  <img src={fileUrl(current.before_image_url)} alt="" className="h-full w-full object-cover" />
                  <img src={fileUrl(current.after_image_url)} alt="" className="h-full w-full object-cover" />
                  <span className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[9px] font-bold text-center leading-4">B / A</span>
                </button>
                {current.extra_images.map((url, i) => (
                  <button key={i} onClick={() => setLightboxView(url)} data-testid={`lightbox-thumb-${i}`} className={`shrink-0 h-16 w-24 rounded-lg overflow-hidden border-2 ${lightboxView === url ? "border-green-500" : "border-transparent opacity-70 hover:opacity-100"}`}>
                    <img src={fileUrl(url)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4 text-white">
              <h3 className="font-display font-bold text-xl">{current.caption}</h3>
              <div className="flex items-center gap-4 mt-1 text-sm text-slate-300">
                <span className="text-green-400 font-semibold">{current.service_category}</span>
                {current.completion_date && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{current.completion_date}</span>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
